import type { CapturedNetworkRequest, CaptureResult } from 'posthog-js';

/**
 * Retire `code` et `state` des donnees envoyees a PostHog (retour OAuth de Discord sur
 * `/settings/discord/callback?code=...&state=...`). Le `code` ne s'echange qu'avec le secret
 * du client Discord et est consomme en une seconde, mais il n'a rien a faire dans un outil
 * de mesure. Fonctions pures, branchees dans `main.ts` sur `before_send` et sur
 * `session_recording.maskCapturedNetworkRequestFn`.
 */

const OAUTH_PARAM = /([?&](?:code|state)=)[^&#]*/g;
const REDACTED = '[redacted]';

/** Proprietes d'evenement qui portent une adresse complete, requete comprise. */
const URL_PROPERTIES: readonly string[] = [
  '$current_url',
  '$referrer',
  '$initial_current_url',
  '$initial_referrer',
  '$session_entry_url',
  '$session_entry_referrer',
];

const SNAPSHOT_PROPERTY = '$snapshot_data';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function scrubOAuthUrl(url: string): string {
  return url.replace(OAUTH_PARAM, `$1${REDACTED}`);
}

/** Evenement de reprise de session : l'adresse est dans `data.href` (evenement « meta »). */
function scrubSnapshotEvent(event: unknown): unknown {
  if (!isRecord(event) || !isRecord(event['data'])) return event;
  const href = event['data']['href'];
  if (typeof href !== 'string') return event;
  return { ...event, data: { ...event['data'], href: scrubOAuthUrl(href) } };
}

function scrubProperty(key: string, value: unknown): unknown {
  if (URL_PROPERTIES.includes(key) && typeof value === 'string') return scrubOAuthUrl(value);
  if (key === SNAPSHOT_PROPERTY && Array.isArray(value)) return value.map(scrubSnapshotEvent);
  return value;
}

function scrubFields(fields: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(fields).map(([key, value]) => [key, scrubProperty(key, value)])
  );
}

/**
 * `before_send` de PostHog : renvoie l'evenement avec les adresses nettoyees, jamais `null` (rien
 * n'est supprime). Les proprietes de personne `$set` et `$set_once` portent les memes adresses
 * (`$initial_current_url`, `$initial_referrer`, ...) a la racine de l'evenement, pas dans `properties`.
 */
export function scrubOAuthCapture(capture: CaptureResult | null): CaptureResult | null {
  if (capture === null) return null;
  const scrubbed: CaptureResult = { ...capture, properties: scrubFields(capture.properties) };
  if (capture.$set !== undefined) scrubbed.$set = scrubFields(capture.$set);
  if (capture.$set_once !== undefined) scrubbed.$set_once = scrubFields(capture.$set_once);
  return scrubbed;
}

/** `maskCapturedNetworkRequestFn` : l'entree de navigation d'une reprise de session porte l'adresse de la page. */
export function scrubOAuthNetworkRequest(request: CapturedNetworkRequest): CapturedNetworkRequest {
  return typeof request.name === 'string'
    ? { ...request, name: scrubOAuthUrl(request.name) }
    : request;
}
